import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { NgIf } from '@angular/common';

import { LayoutComponent } from './shared/components/layout/layout.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { InactivityManagerComponent } from './shared/components/inactivity-manager/inactivity-manager.component';
import { BuyNowRedirectModalComponent } from './shared/components/buy-now-redirect-modal/buy-now-redirect-modal.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { I18nPipe } from './shared/pipes/i18n.pipe';
import { AuthService } from './core/services/auth.service';
import { BuyNowRedirectService } from './core/services/buy-now-redirect.service';
import { LoginService } from './core/services/login.service';
import { CartInitService } from './core/store/cart/cart-init.service';
import { LocationFacade } from './core/store/location/location.facade';
import { GtmService } from './core/services/gtm.service';
import { UserFacade } from './core/store/user/user.facade';
import { UrlCartService } from './shared/services/url-cart.service';
import { UtmPreservationService } from './core/services/utm-preservation.service';
import { CampaignCaptureService } from './core/services/campaign-capture.service';
import { CartChannelScopeService } from './core/services/cart-channel-scope.service';
import { ChannelBootstrapService } from './core/services/channel-bootstrap.service';
import { BlipGoContractService } from './core/services/blip-go-contract.service';
import { ChannelEventsService } from './core/services/channel-events.service';
import { I18nService } from './core/services/i18n.service';
import { ToastService } from './shared/services/toast.service';

@Component({
  selector: 'umkt-root',
  standalone: true,
  imports: [RouterOutlet, LayoutComponent, NgIf, ToastComponent, InactivityManagerComponent, BuyNowRedirectModalComponent, LoadingComponent, I18nPipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'umarketplace-webclient-pme';
  isAuthPingInProgress = false;

  private router = inject(Router);
  private authService = inject(AuthService);
  private loginService = inject(LoginService);
  private cartInitService = inject(CartInitService);
  private locationFacade = inject(LocationFacade);
  private gtm = inject(GtmService);
  private userFacade = inject(UserFacade);
  private urlCartService = inject(UrlCartService);
  private utmPreservationService = inject(UtmPreservationService);
  private campaignCaptureService = inject(CampaignCaptureService);
  private cartChannelScopeService = inject(CartChannelScopeService);
  private channelBootstrapService = inject(ChannelBootstrapService);
  private blipGoContractService = inject(BlipGoContractService);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private channelEventsService = inject(ChannelEventsService);
  buyNowRedirectService = inject(BuyNowRedirectService);

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0 });
        }
        this.gtm.pushPageView(window.location.href, document.title, event.urlAfterRedirects);
      }
    });
  }

  ngOnInit(): void {
    if (this.getCodeFromUrl()) {
      this.isAuthPingInProgress = true;
    }
    localStorage.setItem('lang', 'pt-BR');
    
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    this.userFacade.getUserUuid().subscribe(userUuid => {
      this.gtm.setUser(userUuid || undefined);
    });

    this.authService.initializeAuthState();
    this.cartInitService.initializeCart();
    this.locationFacade.loadLocationFromStorage();

    this.utmPreservationService.initialize();
    this.channelBootstrapService.initialize();
    this.campaignCaptureService.initialize();
    this.cartChannelScopeService.initialize();
    this.blipGoContractService.initialize();
    
    // Aguarda um pouco para garantir que a inicialização do carrinho seja concluída
    // antes de processar a adição via URL
    setTimeout(() => {
      this.urlCartService.checkForDirectAddToCart();
      this.processAuthCodeIfPresent();
    }, 100);
  }

  /**
   * Se a URL contiver ?code= (retorno do login via agente externo), chama authPing,
   * exibe loading em tela cheia e, em caso de sucesso, redireciona para select-fixa-contract.
   */
  private processAuthCodeIfPresent(): void {
    const code = this.getCodeFromUrl();
    if (!code) {
      return;
    }

    const handleSuccess = (response: any) => {
      this.authService.handleAuthPingSuccess(response);
      const r = response as Record<string, unknown> | null | undefined;
      const loginAccess =
        (typeof r?.['document'] === 'string' && r['document']) ||
        (typeof r?.['companyName'] === 'string' && r['companyName']) ||
        undefined;
      this.channelEventsService
        .sendUserLoginEvent({
          method: 'PING',
          userUuid: this.authService.getCurrentUserUuid() || undefined,
          login: loginAccess,
          email: loginAccess,
        })
        .subscribe();
      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({});
      this.router.navigate(['/select-fixa-contract'], { queryParams, replaceUrl: true });
      this.isAuthPingInProgress = false;
    };

    const tryPingWithSessionId = () => {
      console.log('tryPingWithSessionId');
      const sessionId = this.authService.getCurrentSessionId();
      console.log('sessionId', sessionId);
      if (!sessionId) {
        this.isAuthPingInProgress = false;
        return;
      }

      this.loginService.authPing(sessionId).subscribe({
        next: (response) => {
          handleSuccess(response);
        },
        error: () => {
          this.isAuthPingInProgress = false;
        },
      });
    };

    this.isAuthPingInProgress = true;
    this.loginService.authPing(code).subscribe({
      next: (response) => {
        const r = response as any;
        const hasStatusField = r && typeof r === 'object' && 'status' in r;
        const isErrorStatus =
          hasStatusField &&
          r.status !== 'success' &&
          r.status !== 200 &&
          r.status !== '200';

        if (isErrorStatus) {
          tryPingWithSessionId();
          return;
        }

        handleSuccess(response);
      },
      error: (err: unknown) => {
        const cnpj = this.extractDocumentIdFromPingError(err);
        if (cnpj) {
          this.authService.logoutFixaByCnpj(cnpj);
        }
        this.authService.logout();
        this.toastService.showError(
          'Não foi possível encontrar contratos para este acesso. Por favor, faça login novamente.',
          5000,
          'top-center'
        );
        this.isAuthPingInProgress = false;
        this.router.navigate(['/login'], { replaceUrl: true });
      },
    });
  }

  private getCodeFromUrl(): string | null {
    const params = this.router.parseUrl(this.router.url).queryParams;
    const code = params['code'];
    return typeof code === 'string' && code.length > 0 ? code : null;
  }

  private extractDocumentIdFromPingError(err: unknown): string | null {
    const body = (err as { error?: { msg?: string; errors?: unknown[] } })?.error;
    if (!body) return null;
    const toSearch = [
      body.msg,
      ...(Array.isArray(body.errors) ? body.errors.filter((e): e is string => typeof e === 'string') : []),
    ].filter(Boolean) as string[];
    const re = /User:\s*(.+?)\s*Contracts not found/i;
    for (const text of toSearch) {
      const match = text.match(re);
      if (match) {
        const raw = match[1].trim().replace(/\s+/g, '');
        const digits = raw.replace(/\D/g, '');
        if (digits.length === 14) return digits;
        if (digits.length > 14) return digits.slice(0, 14);
        return digits || null;
      }
    }
    return null;
  }

  isAuthRoute(): boolean {
    const url = this.router.url.split('?')[0];
    
    return url === '/login' ||
           url.startsWith('/login/cnpj/') ||
           url === '/register' ||
           url.startsWith('/register') ||
           url === '/success-register' ||
           url === '/forgot-password' ||
           url.startsWith('/confirm-email') ||
           url.startsWith('/reset-password') ||
           url === '/select-fixa-contract' ||
           url === '/segment-selection' ||
           url === '/external-channel-complete-address' ||
            url === '/agendeumavisita' ||
           url === '/grupoboticario' ||
           url.startsWith('/grupoboticario/');
  }
}
